import React, { useState } from 'react'
import { usePlayerStore, useAuthStore } from '../stores'
import { Player } from '../types'
import { useProStatus } from '../utils/useProStatus'
import { Upload, CheckCircle, AlertCircle, Loader2, Download } from 'lucide-react'
import { resolveTeamCategory } from '../utils/teamCategory'
import { read, utils } from 'xlsx'

function CSVImporter() {
  const { addPlayers, addClub, clubs } = usePlayerStore()
  const { user } = useAuthStore()
  const { isPro } = useProStatus()
  const [importType, setImportType] = useState<'players' | 'clubs'>('players')
  const [importing, setImporting] = useState(false)
  const [results, setResults] = useState<{ success: number; errors: string[] } | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const canImportClubs = user?.role === 'admin'

  const downloadSampleCSV = () => {
    let csv = ''
    
    if (importType === 'players') {
      // Header row
      csv = 'name,age,positions,club,league,state,foot,potential,team_category,jersey_number,status,goals,assists,apps,shots,kp,dr,du,mins,height,weight,technical,physical,mental,notes\n'
      
      // Sample rows with realistic player data
      const samplePlayers = [
        'Kelechi Iheanacho,27,CF,Scoot FC,Serie A,Lagos,Left,Elite Academy,First Team,14,Active,10,4,25,45,12,8,6,2250,188,82,8,8,8,Strong finisher',
        'Taiwo Awoniyi,26,CF,Scoot FC,Serie A,Kwara,Right,National Level,First Team,9,Active,15,2,28,52,8,4,3,2520,186,9,8,8,7,Clinical striker',
        'Frank Onyeka,26,CDM,Scoot FC,Serie A,Lagos,Right,National Level,First Team,15,Active,1,3,24,8,18,12,14,1920,180,8,8,8,8,Box-to-box midfielder',
        'Emeka Okafor,19,CB,Scoot FC,NLO,Anambra,Right,Regional Talent,U-19,45,Active,0,0,12,1,2,18,25,720,185,7,8,7,Promising defender',
        'Chinedu Eze,18,CF,Scoot FC,NLO,Enugu,Right,International Potential,U-19,39,Active,5,1,10,18,4,2,1,600,179,7,7,6,Good technical ability',
      ]
      csv += samplePlayers.join('\n')
    } else {
      // Club import template
      csv = 'name,location\n'
      const sampleClubs = [
        'Scoot FC,Lagos',
        'Rivers United,Port Harcourt',
        'Kano Pillars,Kano',
        'Enyimba FC,Aba',
        'Sunshine Stars,Akure',
      ]
      csv += sampleClubs.join('\n')
    }

    // Create a Blob and download
    const element = document.createElement('a')
    const file = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    element.href = URL.createObjectURL(file)
    element.download = `sample_${importType}_import.csv`
    element.click()
    URL.revokeObjectURL(element.href)
  }

  const handleFile = async (file: File) => {
    const name = file.name.toLowerCase()
    const isCsv = name.endsWith('.csv')
    const isExcel = name.endsWith('.xls') || name.endsWith('.xlsx')

    if (!isCsv && !isExcel) {
      alert('Please upload a .csv, .xls, or .xlsx file')
      return
    }

    setImporting(true)
    setResults(null)

    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        let text = ''
        if (isExcel) {
          const data = new Uint8Array(e.target?.result as ArrayBuffer)
          const workbook = read(data, { type: 'array' })
          const firstSheet = workbook.SheetNames[0]
          text = utils.sheet_to_csv(workbook.Sheets[firstSheet])
        } else {
          const decoder = new TextDecoder('utf-8')
          text = decoder.decode(e.target?.result as ArrayBuffer)
        }

        const lines = text.split('\n').filter(line => line.trim())
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
        
        const newPlayers: Player[] = []
        const errors: string[] = []
        // Find the club record for the current user if in club mode
        const userClub = user?.role === 'club' && user?.id ? clubs.find(club => club.id === user.id || (club as any).scout_id === user.id || club.scoutId === user.id) : null

        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim())
          if (values.length < headers.length) continue

          const row: any = {}
          headers.forEach((header, index) => {
            row[header] = values[index]
          })

          try {
            const age = parseInt(row.age) || 18
            const player: any = {
              id: crypto.randomUUID(),
              name: row.name || 'Unknown Player',
              age,
              positions: row.positions ? row.positions.split(';').map((p: string) => p.trim()) : [],
              main_position: row.position || row.main_position || '',
              club: row.club || '',
              league: row.league || '',
              competitions: row.competitions
                ? row.competitions.split(';').map((competition: string) => competition.trim()).filter(Boolean)
                : (row.league ? [row.league] : []),
              state: row.state || '',
              lga: row.lga || '',
              foot: row.foot || 'Right',
              height: row.height ? parseFloat(row.height) : undefined,
              weight: row.weight ? parseInt(row.weight) : undefined,
              goals: parseInt(row.goals || '0'),
              assists: parseInt(row.assists || '0'),
              apps: parseInt(row.apps || '0'),
              shots: parseInt(row.shots || '0'),
              kp: parseInt(row.kp || '0'),
              dr: parseInt(row.dr || '0'),
              du: parseInt(row.du || '0'),
              mins: parseInt(row.mins || '0'),
              potential: (row.potential || 'Local Prospect') as any,
              technical: parseInt(row.technical || '5'),
              physical: parseInt(row.physical || '5'),
              mental: parseInt(row.mental || '5'),
              notes: row.notes || '',
              match_desc: row.match_desc || '',
              youtube: row.youtube || null,
              photo_url: row.photo_url || null,
              scout: user?.email || 'Bulk Import',
              owner_club_id: user?.role === 'club' ? (userClub?.id || null) : undefined,
              jersey_number: row.jersey_number ? parseInt(row.jersey_number) : undefined,
              team_category: user?.role === 'club' ? resolveTeamCategory(row.team_category, age) : (row.team_category || undefined),
              status: row.status || (user?.role === 'club' ? 'Active' : undefined),
              is_public: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
            newPlayers.push(player)
          } catch (err) {
            errors.push(`Row ${i + 1}: ${err instanceof Error ? err.message : 'Invalid data'}`)
          }
        }

        if (importType === 'players' && newPlayers.length > 0) {
          try {
            await addPlayers(newPlayers)
            setResults({ success: newPlayers.length, errors })
          } catch (err: any) {
            const apiErrors = err.message.split('; ')
            setResults({ 
              success: newPlayers.length - apiErrors.length, 
              errors: [...errors, ...apiErrors] 
            })
          }
        } else if (importType === 'clubs') {
          if (!canImportClubs) {
            setResults({ success: 0, errors: ['Club imports are restricted to admin accounts.'] })
            return
          }
          let successCount = 0
          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim())
            if (values.length < 2) continue
            
            try {
              await addClub({
                id: crypto.randomUUID(),
                name: values[0],
                location: values[1] || '',
                scoutId: undefined, // User type doesn't have id yet, using undefined
                is_visible: true,
                is_verified: false,
                created_at: new Date().toISOString()
              })
              successCount++
            } catch (err) {
              errors.push(`Row ${i + 1}: Failed to create club`)
            }
          }
          setResults({ success: successCount, errors })
        }
      } catch (err) {
        setResults({ success: 0, errors: ['Failed to parse file. Ensure it follows the required format.'] })
      } finally {
        setImporting(false)
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const onDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true)
    else if (e.type === 'dragleave') setDragActive(false)
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {!isPro && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6 text-center">
          <AlertCircle size={24} className="text-yellow-400 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-yellow-300">Pro Feature</h3>
          <p className="text-sm text-yellow-400/80 mt-1">Bulk CSV import is available for Pro subscribers only.</p>
          <a href="/pricing" className="mt-4 inline-block px-5 py-2 bg-yellow-500 text-black font-bold rounded-xl hover:bg-yellow-400 transition text-sm">Upgrade to Pro</a>
        </div>
      )}
      {isPro && (
      <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-8 shadow-xl">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-600/20 rounded-2xl flex items-center justify-center">
              <Upload className="text-green-500" size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-white italic uppercase tracking-tight">Bulk {importType === 'players' ? 'Player' : 'Club'} Import</h2>
              <p className="text-gray-500 font-medium">Upload a CSV or Excel file to load multiple records at once</p>
            </div>
          </div>
          <div className="flex bg-[#0b0f1a] p-1 rounded-xl border border-gray-800">
            <button
              onClick={() => { setImportType('players'); setResults(null); }}
              className={`px-4 py-2 rounded-lg text-xs font-black uppercase transition ${importType === 'players' ? 'bg-green-600 text-white shadow-lg shadow-green-600/20' : 'text-gray-500 hover:text-white'}`}
            >
              Players
            </button>
            {canImportClubs && (
              <button
                onClick={() => { setImportType('clubs'); setResults(null); }}
                className={`px-4 py-2 rounded-lg text-xs font-black uppercase transition ${importType === 'clubs' ? 'bg-green-600 text-white shadow-lg shadow-green-600/20' : 'text-gray-500 hover:text-white'}`}
              >
                Clubs
              </button>
            )}
          </div>
        </div>

        {/* Template Info */}
        <div className="bg-[#0b0f1a] border border-gray-800 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-black text-white uppercase tracking-widest">Required Columns</h3>
            <button
              onClick={downloadSampleCSV}
              className="flex items-center gap-2 px-4 py-2 bg-green-600/20 border border-green-600/50 rounded-lg hover:bg-green-600/30 transition text-xs font-bold text-green-500 hover:text-green-400"
            >
              <Download size={16} />
              Download Sample
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {importType === 'players' 
              ? ['name', 'age', 'positions', 'club', 'league', 'state', 'foot', 'potential'].map(header => (
                  <span key={header} className="px-3 py-1 bg-gray-800 border border-gray-700 rounded-lg text-xs font-mono text-gray-400">
                    {header}
                  </span>
                ))
              : ['name', 'location'].map(header => (
                  <span key={header} className="px-3 py-1 bg-gray-800 border border-gray-700 rounded-lg text-xs font-mono text-gray-400">
                    {header}
                  </span>
                ))
            }
          </div>
          <p className="mt-4 text-xs text-gray-500 italic">
            {importType === 'players' 
              ? 'Separate multiple positions with semicolons (e.g., "CM; CAM; CDM")' 
              : canImportClubs
                ? 'Include club name and city/state (e.g., "Rivers United", "Port Harcourt")'
                : 'Club imports are available to admin accounts only.'}
          </p>
        </div>

        {/* Drop Zone */}
        {!results && !importing && (
          <div
            onDragEnter={onDrag}
            onDragLeave={onDrag}
            onDragOver={onDrag}
            onDrop={onDrop}
            className={`relative border-2 border-dashed rounded-3xl p-12 text-center transition-all ${
              dragActive 
                ? 'border-green-500 bg-green-500/5' 
                : 'border-gray-800 bg-[#0d1117] hover:border-gray-700'
            }`}
          >
            <input
              type="file"
              accept=".csv,.xls,.xlsx"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <Upload className="text-gray-400" size={32} />
              </div>
              <p className="text-lg font-bold text-white mb-1">Drag and drop your file here</p>
              <p className="text-gray-500">or click to browse from your device (.csv, .xls, .xlsx)</p>
            </div>
          </div>
        )}

        {/* Importing State */}
        {importing && (
          <div className="bg-[#0d1117] border border-gray-800 rounded-3xl p-12 text-center">
            <Loader2 className="w-12 h-12 text-green-500 animate-spin mx-auto mb-4" />
            <p className="text-lg font-bold text-white">Processing Data...</p>
            <p className="text-gray-500 text-sm mt-2">Validing records and syncing with database</p>
          </div>
        )}

        {/* Results Summary */}
        {results && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-green-900/10 border border-green-800/30 rounded-2xl p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/20">
                  <CheckCircle className="text-white" size={24} />
                </div>
                <div>
                  <p className="text-3xl font-black text-white">{results.success}</p>
                  <p className="text-sm font-bold text-green-500 uppercase tracking-wider">{importType === 'players' ? 'Players' : 'Clubs'} Imported Successfully</p>
                </div>
              </div>

              {results.errors.length > 0 && (
                <div className="bg-red-900/10 border border-red-800/30 rounded-2xl p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center shadow-lg shadow-red-500/20">
                    <AlertCircle className="text-white" size={24} />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-white">{results.errors.length}</p>
                    <p className="text-sm font-bold text-red-500 uppercase tracking-wider">Rows Had Errors</p>
                  </div>
                </div>
              )}
            </div>

            {results.errors.length > 0 && (
              <div className="bg-[#0d1117] border border-gray-800 rounded-2xl p-6">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Error Log</h3>
                <div className="space-y-2 max-h-40 overflow-y-auto pr-2 custom-scrollbar">
                  {results.errors.map((err, i) => (
                    <p key={i} className="text-xs text-red-400 font-mono bg-red-950/20 p-2 rounded border border-red-900/20">
                      {err}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => setResults(null)}
              className="w-full py-4 bg-gray-800 hover:bg-gray-700 rounded-xl font-bold text-white transition border border-gray-700"
            >
              Import Another File
            </button>
          </div>
        )}
      </div>
      )}

      {/* Pro Tips */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { title: 'Formatting', text: 'Ensure your file is properly formatted. Excel files (.xls/.xlsx) will use the first sheet automatically.' },
          { title: 'Performance', text: 'You can import up to 500 players in a single session.' }
        ].map(tip => (
          <div key={tip.title} className="p-6 bg-[#161b22] border border-gray-800 rounded-2xl">
            <h4 className="text-xs font-black text-white uppercase tracking-widest mb-2">{tip.title}</h4>
            <p className="text-sm text-gray-500 leading-relaxed">{tip.text}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

export default CSVImporter
